import { useState } from "react";
import { Search, ChevronRight, Undo2, FileText, Loader2, Plus, Link as LinkIcon, Pencil, Tag } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import PriceEditModal from "./PriceEditModal";
import PaymentLinkDialog from "./PaymentLinkDialog";
import ManualQuoteForm from "./ManualQuoteForm";

const money = (n) => (n === undefined || n === null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`);

const STATUS_ORDER = [
  "manual_quote_created",
  "quote_sent",
  "quote_generated",
  "awaiting_payment",
  "deposit_paid",
  "awaiting_allocation",
  "confirmed",
  "collected",
  "delivered",
  "cancelled",
  "refund_required",
  "refunded",
];

const NEXT_STATUS = {
  manual_quote_created: "quote_sent",
  quote_sent: "deposit_paid",
  deposit_paid: "awaiting_allocation",
  awaiting_allocation: "confirmed",
  confirmed: "collected",
  collected: "delivered",
};

const EXTRA_FILTERS = [
  { key: "all", label: "All" },
  { key: "auto", label: "Automatic quote" },
  { key: "manual", label: "Manual quote" },
  { key: "override", label: "Manual price override" },
  { key: "paid", label: "Deposit paid" },
  { key: "unpaid", label: "Unpaid" },
];

export default function BookingsPanel({ items, reload }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [busyRef, setBusyRef] = useState(null);
  const [priceEdit, setPriceEdit] = useState(null);   // booking object
  const [paymentLink, setPaymentLink] = useState(null); // booking object
  const [manualOpen, setManualOpen] = useState(false);

  if (items === null) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E60000]" />
      </div>
    );
  }

  async function onStatusChange(ref, status) {
    try {
      await apiClient.patch(`/admin/bookings/${ref}/status`, { status });
      toast.success(`${ref} → ${status.replace(/_/g, " ")}`);
      reload();
    } catch (e) {
      toast.error("Status update failed.");
    }
  }

  async function onRefund(ref) {
    if (!window.confirm(`Refund the deposit for ${ref}? This action cannot be undone.`)) return;
    setBusyRef(ref);
    try {
      await apiClient.post(`/admin/bookings/${ref}/refund`);
      toast.success(`Refund initiated for ${ref}`);
      reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Refund failed.";
      toast.error(typeof msg === "string" ? msg : "Refund failed.");
    } finally {
      setBusyRef(null);
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = items.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (typeFilter === "manual" && !b.is_manual_quote) return false;
    if (typeFilter === "auto" && b.is_manual_quote) return false;
    if (typeFilter === "override" && b.manual_price_override == null) return false;
    if (typeFilter === "paid" && b.payment_status !== "paid" && b.payment_status !== "paid_in_full") return false;
    if (typeFilter === "unpaid" && (b.payment_status === "paid" || b.payment_status === "paid_in_full")) return false;
    if (!q) return true;
    const hay = [
      b.booking_ref, b.customer_name, b.customer_phone, b.customer_email,
      b.pickup_location, b.dropoff_location, b.vehicle_make, b.vehicle_model,
      b.vehicle_registration, b.requested_collection_date,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const allCount = items.length;
  const emptyState = items.length === 0;

  return (
    <div data-testid="admin-bookings-list">
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="admin-bookings-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ref, name, phone, email, reg, postcode…"
            className="w-full bg-transparent border border-white/15 text-white pl-10 pr-4 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors"
          />
        </div>
        <select
          data-testid="admin-bookings-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-transparent border border-white/15 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E60000] transition-colors appearance-none cursor-pointer min-w-[200px]"
        >
          <option className="bg-[#0A0A0A]" value="all">All statuses ({allCount})</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} className="bg-[#0A0A0A]" value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
        <select
          data-testid="admin-bookings-type-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-transparent border border-white/15 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E60000] transition-colors appearance-none cursor-pointer min-w-[180px]"
        >
          {EXTRA_FILTERS.map((f) => (
            <option key={f.key} className="bg-[#0A0A0A]" value={f.key}>{f.label}</option>
          ))}
        </select>
        <button
          data-testid="admin-create-manual-quote"
          onClick={() => setManualOpen(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> Create manual quote
        </button>
      </div>

      {emptyState ? (
        <div className="border border-white/10 bg-[#0A0A0A] p-16 text-center text-zinc-500 text-sm">
          No bookings yet — create a manual quote to get started.
        </div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 gap-4 bg-[#0A0A0A] px-6 py-4 text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold border-b border-white/10">
            <div className="col-span-2">Ref · Status</div>
            <div className="col-span-2">Customer</div>
            <div className="col-span-3">Route · Vehicle</div>
            <div className="col-span-2">Collect</div>
            <div className="col-span-3 text-right">Price · Actions</div>
          </div>

          {filtered.length === 0 ? (
            <div className="px-6 py-16 text-center text-zinc-500 text-sm">
              No bookings match your search.
            </div>
          ) : filtered.map((b) => (
            <BookingRow
              key={b.booking_id || b.booking_ref}
              b={b}
              busy={busyRef === b.booking_ref}
              onStatusChange={onStatusChange}
              onRefund={onRefund}
              onEditPrice={() => setPriceEdit(b)}
              onPaymentLink={() => setPaymentLink(b)}
            />
          ))}
        </div>
      )}

      {priceEdit && (
        <PriceEditModal
          booking={priceEdit}
          onClose={() => setPriceEdit(null)}
          onSaved={() => { setPriceEdit(null); reload(); }}
        />
      )}
      {paymentLink && (
        <PaymentLinkDialog
          booking={paymentLink}
          onClose={() => { setPaymentLink(null); reload(); }}
        />
      )}
      {manualOpen && (
        <ManualQuoteForm
          onClose={() => setManualOpen(false)}
          onCreated={() => { setManualOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

function BookingRow({ b, busy, onStatusChange, onRefund, onEditPrice, onPaymentLink }) {
  const nxt = NEXT_STATUS[b.status];
  const hasOverride = b.manual_price_override != null;
  const current = hasOverride ? b.manual_price_override : b.instant_booking_price;
  const original = b.original_instant_booking_price ?? b.instant_booking_price;
  const diff = hasOverride ? Number(current) - Number(original) : 0;

  return (
    <div
      data-testid={`admin-booking-${b.booking_ref}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 hover:bg-[#0A0A0A] transition-colors"
    >
      <div className="lg:col-span-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-bold text-white">{b.booking_ref}</div>
          {b.is_manual_quote && (
            <span data-testid={`badge-manual-${b.booking_ref}`} className="text-[9px] tracking-[0.2em] uppercase font-bold text-[#E60000] border border-[#E60000]/40 px-1.5 py-0.5">
              MANUAL
            </span>
          )}
          {hasOverride && (
            <span data-testid={`badge-override-${b.booking_ref}`} className="text-[9px] tracking-[0.2em] uppercase font-bold text-[#E60000] bg-[#E60000]/10 border border-[#E60000]/40 px-1.5 py-0.5">
              MANUAL PRICE
            </span>
          )}
        </div>
        <select
          value={b.status}
          onChange={(e) => onStatusChange(b.booking_ref, e.target.value)}
          data-testid={`admin-booking-${b.booking_ref}-status`}
          className="mt-1 bg-transparent border border-white/15 text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold px-2 py-1 cursor-pointer focus:outline-none focus:border-[#E60000]"
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} className="bg-[#0A0A0A] text-white" value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
        {b.flags?.length ? (
          <div className="mt-1 text-[10px] text-yellow-500">⚑ {b.flags.length} flag(s)</div>
        ) : null}
      </div>
      <div className="lg:col-span-2 text-sm">
        <div className="font-semibold">{b.customer_name}</div>
        <div className="text-zinc-500 text-xs">{b.customer_phone}</div>
        <div className="text-zinc-500 text-xs break-all">{b.customer_email}</div>
      </div>
      <div className="lg:col-span-3 text-sm">
        <div>
          <span className="text-zinc-500">From</span> {b.pickup_location}{" "}
          <span className="text-zinc-500">→</span> {b.dropoff_location}
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          {b.vehicle_year} {b.vehicle_make} {b.vehicle_model}
          {b.loaded_miles ? ` · ${b.loaded_miles} mi` : ""}
        </div>
        {b.condition !== "runner" && <div className="text-[10px] text-yellow-500 mt-1">{b.condition}</div>}
        {b.internal_notes && (
          <div className="text-[10px] text-zinc-500 mt-1 italic line-clamp-2">Internal: {b.internal_notes}</div>
        )}
      </div>
      <div className="lg:col-span-2 text-sm">
        <div>{b.requested_collection_date || "—"}</div>
        <div className="text-zinc-500 text-xs">{b.requested_collection_time}</div>
        {b.urgency_label && b.urgency_label !== "planned" && (
          <div className="text-[10px] text-[#E60000] mt-1">{b.urgency_label}</div>
        )}
        {b.quote_expires_at && (
          <div className="text-[10px] text-zinc-500 mt-1">
            Expires: {new Date(b.quote_expires_at).toLocaleString("en-GB")}
          </div>
        )}
      </div>
      <div className="lg:col-span-3 text-right text-sm space-y-2">
        <div>
          <div className="text-white font-bold">{money(current)}</div>
          {hasOverride && (
            <div className="text-[10px] text-zinc-500">
              was {money(original)} · <span className={diff < 0 ? "text-emerald-400" : "text-yellow-400"}>
                {diff < 0 ? "" : "+"}£{diff.toFixed(0)}
              </span>
              {b.manual_override_reason && (
                <span className="text-zinc-500"> · {b.manual_override_reason.replace(/_/g, " ")}</span>
              )}
            </div>
          )}
          <div className="text-xs text-zinc-500">
            Deposit {money(b.deposit_paid || b.deposit_due)} · Bal {money(b.balance_remaining)}
          </div>
        </div>
        <div className="flex justify-end gap-2 items-center flex-wrap">
          <button
            onClick={onEditPrice}
            data-testid={`admin-booking-${b.booking_ref}-edit-price`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 border border-white/15 px-2.5 py-1 hover:border-[#E60000] hover:text-white transition-colors"
          >
            <Pencil className="w-3 h-3" /> Edit price
          </button>
          <button
            onClick={onPaymentLink}
            data-testid={`admin-booking-${b.booking_ref}-payment-link`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 border border-white/15 px-2.5 py-1 hover:border-[#E60000] hover:text-white transition-colors"
          >
            <LinkIcon className="w-3 h-3" /> Payment link
          </button>
          <a
            href={`/quote/${b.booking_ref}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-booking-${b.booking_ref}-view-quote`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 border border-white/15 px-2.5 py-1 hover:border-[#E60000] hover:text-white transition-colors"
          >
            <Tag className="w-3 h-3" /> View quote
          </a>
          {nxt && (
            <button
              onClick={() => onStatusChange(b.booking_ref, nxt)}
              data-testid={`admin-booking-${b.booking_ref}-advance`}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 border border-white/15 px-2.5 py-1 hover:border-[#E60000] hover:text-white transition-colors"
            >
              {nxt.replace(/_/g, " ")} <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {(b.payment_status === "paid" || b.payment_status === "paid_in_full") && (
            <button
              onClick={() => onRefund(b.booking_ref)}
              disabled={busy}
              data-testid={`admin-booking-${b.booking_ref}-refund`}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 border border-white/15 px-2.5 py-1 hover:border-[#E60000] hover:text-[#E60000] transition-colors disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Refund
            </button>
          )}
          <a
            href={`/admin/bookings/${b.booking_ref}/sheet`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-booking-${b.booking_ref}-sheet`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E60000] hover:bg-[#FF1A1A] px-2.5 py-1 transition-colors"
          >
            <FileText className="w-3 h-3" /> Sheet
          </a>
        </div>
      </div>
    </div>
  );
}
